/*
 * Copyright (c) 2025 Sila Artificial Intelligence Research LLC
 * A DA Holding Ltd Company
 * All rights reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 * This software and its documentation are proprietary to Sila Artificial
 * Intelligence Research LLC. Unauthorized copying, distribution, or use
 * of this software is strictly prohibited.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL
 * THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING
 * FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER
 * DEALINGS IN THE SOFTWARE.
 */

/**
 * Chat Service for BrandHealth 360
 * Handles all chat-related API calls and session management
 */

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp?: string;
  visualizations?: any[];
}

export interface ChatSession {
  id: string;
  title: string;
  brand_id?: string;
  last_message: string;
  message_count: number;
  created_at: string;
  updated_at: string;
}

export interface ChatSessionDetail {
  id: string;
  title: string;
  brand_id?: string;
  messages: ChatMessage[];
  created_at: string;
  updated_at: string;
}

class ChatService {
  private baseUrl = '/api/chat';

  /**
   * Get recent chat sessions
   */
  async getRecentChats(brandId?: string, limit: number = 20): Promise<ChatSession[]> {
    try {
      const params = new URLSearchParams();
      if (brandId) params.append('brand_id', brandId);
      params.append('limit', limit.toString());
      
      const response = await fetch(`${this.baseUrl}/recent-noauth?${params}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch recent chats: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching recent chats:', error);
      throw error;
    }
  }

  /**
   * Get a specific chat session with all messages
   */
  async getChatSession(chatId: string): Promise<ChatSessionDetail> {
    try {
      const response = await fetch(`${this.baseUrl}/sessions-noauth/${chatId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch chat session: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching chat session:', error);
      throw error;
    }
  }

  /**
   * Delete a chat session
   */
  async deleteChatSession(chatId: string): Promise<void> {
    try {
      const response = await fetch(`${this.baseUrl}/sessions-noauth/${chatId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to delete chat session: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error deleting chat session:', error);
      throw error;
    }
  }

  /**
   * Save a new chat session or update existing one
   */
  async saveChatSession(
    chatId: string,
    brandId: string,
    messages: ChatMessage[],
    userId: string = 'demo_user'
  ): Promise<void> {
    try {
      const response = await fetch(`${this.baseUrl}/sessions-noauth`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          chat_id: chatId,
          brand_id: brandId,
          user_id: userId,
          messages: messages,
          updated_at: new Date().toISOString(),
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to save chat session: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error saving chat session:', error);
      // Don't throw - we don't want to break the chat flow if saving fails
    }
  }

  /**
   * Generate a unique chat ID
   */
  generateChatId(): string {
    return `chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Search chat sessions
   */
  async searchChats(query: string, brandId?: string): Promise<ChatSession[]> {
    // For now, we'll fetch all chats and filter client-side
    // In the future, this could be a dedicated backend endpoint
    const chats = await this.getRecentChats(brandId, 100);
    
    const searchTerm = query.toLowerCase();
    return chats.filter(chat => 
      chat.title.toLowerCase().includes(searchTerm) ||
      chat.last_message.toLowerCase().includes(searchTerm)
    );
  }
}

export default new ChatService();